"use client";

import { CheckCircleIcon, ClockIcon, CircleDashedIcon, XCircleIcon, type Icon } from "@phosphor-icons/react";
import { useState } from "react";

import { TASK_STEP_PHASE_PRESENTATION } from "@/src/components/tasks/task-step-phase";
import { VerdictButton } from "@/src/components/tasks/verdict-button";
import { ValerieCard, type ValerieCardHeaderVariant } from "@/src/components/valerie-card";
import { toast } from "@/src/hooks/use-toast";
import {
  useTaskStepVerifications,
  useUpsertTaskStepVerificationMutation,
} from "@/src/lib/data-fetchers/tasks-fetchers";
import {
  resolveTaskStepPhase,
  TaskStepCardInactiveStatus,
  TaskStepPhase,
  type PinnableTaskStepPhase,
  type TaskStepCardState,
} from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";

type TaskStepCardPresentation = {
  collapsed?: boolean;
  description: string;
  icon: Icon;
  label: string;
  variant: ValerieCardHeaderVariant;
  /** Strikes the card title, marking a step a human reviewed and rejected. */
  strikeHeader?: boolean;
};

const PHASE_DESCRIPTION: Record<TaskStepPhase, string> = {
  [TaskStepPhase.QA]: "Automation completed this step. Spot-check it below, then mark it correct or incorrect.",
  [TaskStepPhase.ACTION_NEEDED]: "Automation can't finish this one. Complete it in Athena, then continue.",
  [TaskStepPhase.FAILED]: "Automation should have completed this step and didn't. Fix it in Athena, then continue.",
};

export function TaskStepCard({
  header,
  status,
  phase: pinnedPhase,
  detail,
  taskId,
  stepKey,
  workflowStepKeys,
  onVerificationComplete,
  action,
  children,
}: {
  header: React.ReactNode;
  status: TaskStepCardState;
  /**
   * Forces the phase this step presents in while it is asking something of ops, regardless of how
   * cleanly automation resolved it. Pin `ACTION_NEEDED` on a step automation was never built to
   * finish, so a "completed" workflow step can't pass it off as QA'd. A failed sync still
   * outranks the pin. Omit it and the phase follows the status.
   */
  phase?: PinnableTaskStepPhase;
  /**
   * Replaces the card's standard instruction with one that says what *this* step needs.
   *
   * The generic wording can't be specific enough on its own: "Automation can't finish this one"
   * doesn't say it's the emergency contact, and "Waiting" doesn't say which step is blocking —
   * the cards are numbered in the order ops reads them, not the order the workflow runs them.
   * A recorded verdict still overrides this; by then the outcome is the story.
   */
  detail?: string;
  taskId: string;
  stepKey: string;
  workflowStepKeys: string[];
  /**
   * Releases whatever the workflow is waiting on, called only when this card's verdict button is
   * the thing that releases it: on a `CORRECT` verdict, and only where the card has no separate
   * continuation control of its own.
   *
   * Both halves matter. Firing it on `INCORRECT` approved the gate ops had just rejected — they
   * clicked Incorrect *because* the coverage was wrong and the workflow continued on it anyway.
   * Firing it alongside a continuation control duplicated that control's own release, or called
   * for an approval that was never pending and failed.
   */
  onVerificationComplete?: (verdict: VerificationResult) => Promise<void>;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const resolvedPhase = resolveTaskStepPhase(status, pinnedPhase);
  const basePresentation = getTaskStepCardPresentation(status, resolvedPhase, detail);
  const { verdictByStepKey, isPending: verdictsPending } = useTaskStepVerifications(taskId);
  /**
   * Which phases let ops record something, and why each one does.
   *
   * `QA` is ops ruling on what automation produced. `ACTION_NEEDED` is ops confirming they did
   * the work themselves, `FAILED` that they fixed it — but only when the caller has no
   * continuation of its own, because that continuation is what releases the workflow and this
   * would be a second, weaker button beside it that releases nothing.
   *
   * A stale row on any other phase is ignored so it can't recolour a card that has moved on: a
   * step marked Correct that later fails or parks on a continuation must read Failed / Action
   * needed, not Completed, since that is the card the workflow is waiting on.
   */
  const footerAction =
    resolvedPhase === TaskStepPhase.ACTION_NEEDED || resolvedPhase === TaskStepPhase.FAILED ? action : null;
  const recordsVerdict =
    resolvedPhase === TaskStepPhase.QA ||
    ((resolvedPhase === TaskStepPhase.ACTION_NEEDED || resolvedPhase === TaskStepPhase.FAILED) && !footerAction);
  const verdict = recordsVerdict ? verdictByStepKey.get(stepKey)?.verdict : undefined;
  const presentation = applyVerdictToPresentation(basePresentation, resolvedPhase, verdict);
  const PhaseIcon = presentation.icon;
  /**
   * Settled means correct with nothing left for this card to release. A `CORRECT` row on a card
   * that still owns a pending gate isn't settled — the release behind it failed or never ran — so
   * Correct stays up to retry it; on a gated `QA` card it is the only release on the page. This is
   * the same test TPR's `confirmed` applies.
   */
  const settled = verdict === VerificationResult.CORRECT && !onVerificationComplete;
  const verdictControl =
    recordsVerdict && resolvedPhase && !verdictsPending && !settled ? (
      <TaskStepVerificationControl
        phase={resolvedPhase}
        taskId={taskId}
        stepKey={stepKey}
        workflowStepKeys={workflowStepKeys}
        verdict={verdict}
        onComplete={onVerificationComplete}
      />
    ) : null;
  const footer =
    footerAction || verdictControl ? (
      <div className="flex flex-col gap-3 border-t pt-3">
        {footerAction}
        {verdictControl}
      </div>
    ) : null;

  return (
    <ValerieCard
      title={presentation.strikeHeader ? <span className="line-through">{header}</span> : header}
      variant={presentation.variant}
      className={presentation.collapsed ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined}
      actions={
        // Phase icon then label in the right corner, matching `TaskStatusLabel`'s icon-first
        // order, and drawn in `currentColor` so both stay legible as the header band changes
        // colour underneath them.
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-current/30 bg-background/15 px-2 py-0.5 text-xs font-medium text-current">
          <PhaseIcon weight="fill" className="size-4 shrink-0" />
          {presentation.label}
        </span>
      }
      contentClassName="space-y-3"
    >
      {presentation.collapsed ? (
        <details>
          <summary className="text-sm text-muted-foreground">{presentation.description}</summary>
          <div className="mt-3 space-y-3">{children}</div>
        </details>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{presentation.description}</p>
          {children}
          {footer}
        </>
      )}
    </ValerieCard>
  );
}

/**
 * Folds ops' recorded verdict into how the card presents.
 *
 * A step in a verdict phase deepens to `verified` green only once ops confirms it. That is the
 * only route to the dark green: a `QA` card is already green while it waits, but automation
 * finishing a step is not the same as anyone having signed off on it.
 *
 * A rejected step goes red, the same colour as a sync failure, because both mean "this is wrong
 * and needs fixing". What separates them is that a rejection was a human decision, so the title is
 * struck through and the ✗ stays lit in the footer — a failed sync has neither.
 */
function applyVerdictToPresentation(
  presentation: TaskStepCardPresentation,
  phase: TaskStepPhase | null,
  verdict: VerificationResult | undefined
): TaskStepCardPresentation {
  if (verdict === VerificationResult.CORRECT) {
    // One badge for all three phases: it answers "is this step done?", which reads the same
    // however it got there. How it got done stays in the body, where there is room to say it.
    const description =
      phase === TaskStepPhase.ACTION_NEEDED
        ? "Completed by ops."
        : phase === TaskStepPhase.FAILED
          ? "Fixed by ops."
          : "Verified by ops as correct.";

    return { ...presentation, description, label: "Completed", icon: CheckCircleIcon, variant: "verified" };
  }

  if (verdict === VerificationResult.INCORRECT) {
    return {
      ...presentation,
      // Names the button this phase actually has: only `QA` has one called Correct.
      description: `Marked incorrect by ops. Fix it, then click ${
        phase === TaskStepPhase.QA ? "Correct" : getSingleVerdictLabel(phase)
      }.`,
      icon: XCircleIcon,
      label: "Incorrect",
      variant: "failed",
      strikeHeader: true,
    };
  }

  return presentation;
}

function getTaskStepCardPresentation(
  status: TaskStepCardState,
  phase: TaskStepPhase | null,
  detail?: string
): TaskStepCardPresentation {
  if (phase) {
    const { label, icon, variant } = TASK_STEP_PHASE_PRESENTATION[phase];

    return { description: detail ?? PHASE_DESCRIPTION[phase], icon, label, variant };
  }

  switch (status) {
    case TaskStepCardInactiveStatus.NOT_NEEDED:
      return {
        collapsed: true,
        description: "This step is not needed.",
        icon: CircleDashedIcon,
        label: "Not needed",
        variant: "outline",
      };
    /**
     * `WAITING`, `NOT_RUN`, and the exhaustiveness backstop: a phase covers every actionable
     * status, so anything reaching here is a neutral timeline state.
     *
     * Waiting and not-run are deliberately one presentation. The distinction was never real to
     * ops — both mean "this hasn't happened yet" — and it wasn't even reliable underneath, since
     * `taskStepStatus` returns `waiting` as its catch-all for a step whose row doesn't exist.
     * Two labels for one situation just invited the question of which was which.
     */
    default:
      return {
        description: detail ?? "Waiting for this step to complete.",
        icon: ClockIcon,
        label: "Waiting",
        variant: "outline",
      };
  }
}

/** The one button `ACTION_NEEDED` and `FAILED` offer in place of Correct / Incorrect. */
function getSingleVerdictLabel(phase: TaskStepPhase | null): string {
  return phase === TaskStepPhase.FAILED ? "Continue" : "Updated";
}

/**
 * What ops records in the card footer, which differs by phase because the question does.
 *
 * `QA` asks "did automation get this right?" — a Correct / Incorrect pair. The other two have
 * only one answer worth a button: `ACTION_NEEDED` asks "have you done it?" (**Updated**) and
 * `FAILED` asks "have you fixed it?" (**Continue**). All three write the same verdict row, so a
 * resolved step reads the same way to downstream accuracy reporting however it got there.
 */
function TaskStepVerificationControl({
  phase,
  taskId,
  stepKey,
  workflowStepKeys,
  verdict,
  onComplete,
}: {
  phase: TaskStepPhase;
  taskId: string;
  stepKey: string;
  workflowStepKeys: string[];
  verdict: VerificationResult | undefined;
  onComplete?: (verdict: VerificationResult) => Promise<void>;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const upsertMutation = useUpsertTaskStepVerificationMutation();

  const handleClick = async (next: VerificationResult) => {
    // A rejection records the verdict and nothing else: releasing the workflow on `INCORRECT` is
    // the one thing ops was saying not to do. So re-clicking the recorded verdict is only worth a
    // round trip when there is a release to (re)attempt, which is `CORRECT` alone.
    const releasesWorkflow = next === VerificationResult.CORRECT && Boolean(onComplete);

    if (verdict === next && !releasesWorkflow) return;

    setIsSubmitting(true);
    try {
      // Release before recording: an approval can re-validate and reject, leaving a saved
      // verdict on a stalled workflow. A refused release records nothing; the reverse risk
      // (released but unrecorded) is recoverable by clicking again.
      if (releasesWorkflow) await onComplete?.(next);

      if (verdict !== next) {
        await upsertMutation.mutateAsync({
          taskId,
          payload: { stepKey, workflowStepKeys, verdict: next, phase },
        });
      }
    } catch (error) {
      toast({
        title: releasesWorkflow ? "The workflow couldn't continue, so this step wasn't marked" : "Couldn't save that",
        description: error instanceof Error ? error.message : "Refresh the task and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isSubmitting || upsertMutation.isPending;

  if (phase !== TaskStepPhase.QA) {
    return (
      <div className="mx-auto flex w-full max-w-40">
        <VerdictButton
          label={getSingleVerdictLabel(phase)}
          tone="correct"
          selected={verdict === VerificationResult.CORRECT}
          disabled={isBusy}
          onClick={() => handleClick(VerificationResult.CORRECT)}
        />
      </div>
    );
  }

  const incorrectButton = (
    <VerdictButton
      label="Incorrect"
      tone="incorrect"
      selected={verdict === VerificationResult.INCORRECT}
      disabled={isBusy}
      // Only one control per card may claim cmd+Enter, and it shouldn't be the negative one.
      focusOnCommandEnter={false}
      onClick={() => handleClick(VerificationResult.INCORRECT)}
    />
  );

  return (
    // Sized to sit alongside the continuation controls rather than dominate the card: these repeat
    // on all eleven steps, so a page-width CTA on each one made the timeline read as eleven calls
    // to action instead of a checklist.
    <div className="mx-auto flex w-full max-w-sm gap-3">
      <VerdictButton
        label="Correct"
        tone="correct"
        selected={verdict === VerificationResult.CORRECT}
        disabled={isBusy}
        onClick={() => handleClick(VerificationResult.CORRECT)}
      />
      {incorrectButton}
    </div>
  );
}
